import type { WebSocketMessage, StateUpdateMessage } from '@shared/types';
import useGameStore from '@/store/gameStore';
import toast from 'react-hot-toast';

class WebSocketService {
  private ws: WebSocket | null = null;
  private roomId: string | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private messageQueue: WebSocketMessage[] = [];

  connect(roomId: string): void {
    // If already connected to a different room, disconnect first
    if (this.ws?.readyState === WebSocket.OPEN && this.roomId !== roomId) {
      this.disconnect();
    }

    // If already connected to the same room, do nothing
    if (this.ws?.readyState === WebSocket.OPEN && this.roomId === roomId) {
      return;
    }

    this.roomId = roomId;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host =
      import.meta.env.VITE_WS_HOST ||
      (import.meta.env.DEV ? 'localhost:8787' : 'api.yhgameverse.win');

    // Get JWT token from localStorage
    const token = localStorage.getItem('auth_token');
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    const url = `${protocol}//${host}/api/room/${roomId}/ws${tokenParam}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      useGameStore.getState().setConnected(true);
      useGameStore.getState().resetReconnectAttempts();

      // Send queued messages
      while (this.messageQueue.length > 0) {
        const message = this.messageQueue.shift();
        if (message) {
          this.send(message);
        }
      }

      // Start ping interval
      this.startPingInterval();
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        this.handleMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      useGameStore.getState().setError('Connection error occurred');
    };

    this.ws.onclose = (event) => {
      useGameStore.getState().setConnected(false);
      this.stopPingInterval();

      // Attempt reconnection
      if (this.roomId && !event.wasClean) {
        this.scheduleReconnect();
      }
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'STATE_UPDATE': {
        const stateUpdate = message as StateUpdateMessage;
        useGameStore.getState().setGameState(stateUpdate.state);

        // Show notifications for specific changes
        if (stateUpdate.changes?.includes('phase')) {
          this.notifyPhaseChange(stateUpdate.state.phase);
        }
        break;
      }

      case 'ERROR': {
        const errorMsg = (message as { type: 'ERROR'; error: string }).error;
        toast.error(errorMsg);
        useGameStore.getState().setError(errorMsg);
        break;
      }

      case 'PONG':
        // Handle pong response
        break;

      default:
        // Ignore unhandled message types
        break;
    }
  }

  private notifyPhaseChange(phase: string): void {
    const notifications: Record<string, string> = {
      WRITING: '✍️ Time to write clues!',
      REVIEW: '👀 Reviewing clues for duplicates...',
      GUESSING: '🤔 Time to guess the word!',
      SCORING: '🎯 Round complete!',
      GAME_OVER: '🏆 Game Over!',
    };

    const notification = notifications[phase];
    if (notification) {
      toast(notification);
    }
  }

  send(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Queue message for when connection is established
      this.messageQueue.push(message);
    }
  }

  submitClue(clue: string): void {
    this.send({
      type: 'SUBMIT_CLUE',
      clue,
      timestamp: Date.now(),
    });
  }

  submitGuess(guess: string): void {
    this.send({
      type: 'SUBMIT_GUESS',
      guess,
      timestamp: Date.now(),
    });
  }

  startGame(): void {
    this.send({
      type: 'START_GAME',
      timestamp: Date.now(),
    });
  }

  skipCard(): void {
    this.send({
      type: 'SKIP_CARD',
      timestamp: Date.now(),
    });
  }

  nextRound(): void {
    this.send({
      type: 'NEXT_ROUND',
      timestamp: Date.now(),
    });
  }

  updateSettings(settings: Record<string, unknown>): void {
    this.send({
      type: 'UPDATE_SETTINGS',
      settings,
      timestamp: Date.now(),
    } as WebSocketMessage);
  }

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({
          type: 'PING',
          timestamp: Date.now(),
        });
      }
    }, 30000); // Ping every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private scheduleReconnect(): void {
    const { reconnectAttempts, incrementReconnectAttempts } = useGameStore.getState();

    if (reconnectAttempts >= 5) {
      toast.error('Failed to reconnect. Please refresh the page.');
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);

    toast.loading(`Reconnecting in ${delay / 1000}s...`);
    incrementReconnectAttempts();

    this.reconnectTimeout = setTimeout(() => {
      if (this.roomId) {
        this.connect(this.roomId);
      }
    }, delay);
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPingInterval();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.roomId = null;
    this.messageQueue = [];
  }
}

export default new WebSocketService();
